import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { SwarovskiButton } from "../../components/_shared-components/ui-elements/SwarovskiButton";
import { useMounted } from "../../hooks/use-mounted";
import { STOREFRONT_PATH_PREFIX } from "../../const";
import cn from "classnames";
import { ComparisonCell } from "./pdp-variants/ComparisonCell";
import {
  buildComparisonRows,
  normalizePdpPath,
  variantColumnsFromList,
  type VariantColumn,
} from "./pdp-variants/pdp-variants-utils";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";

type SelectedProduct =
  | {
      _tag: "single-product";
      name: string;
      key: string;
      path: string;
      variants: Array<Record<string, unknown>>;
    }
  | {
      _tag: "bundle-product";
      name: string;
      key: string;
      path: string;
      products: Array<{
        name: string;
        key: string;
        variants: Array<Record<string, unknown>>;
      }>;
    };

type PdpPageResponse = {
  __typename: string;
  page: {
    path: string;
    productKey?: string;
    variantSku?: string;
  };
  dataOrError:
    | { _tag: "Right"; right: { _tag: string; selectedProduct?: SelectedProduct } }
    | { _tag: "Left"; left: unknown }
    | { _tag: "Both"; right: { _tag: string; selectedProduct?: SelectedProduct }; left: unknown };
};

type VariantGroup = {
  id: string;
  title: string;
  variants: Array<VariantColumn>;
};

const PDP_PAGE_TYPES = new Set(["ProductDetailPage", "ProductVariantDetailPage", "BundleDetailPage"]);

function extractVariantGroups(selectedProduct: SelectedProduct | undefined): Array<VariantGroup> {
  if (!selectedProduct) {
    return [];
  }

  if (selectedProduct._tag === "single-product") {
    return [
      {
        id: selectedProduct.key,
        title: selectedProduct.name,
        variants: variantColumnsFromList(selectedProduct.variants),
      },
    ];
  }

  return selectedProduct.products.map(product => ({
    id: product.key,
    title: product.name,
    variants: variantColumnsFromList(product.variants),
  }));
}

function VariantComparisonTable(props: {
  variants: Array<VariantColumn>;
  differencesOnly: boolean;
  pathFilter: string;
  activeVariantSku?: string;
}) {
  const rows = useMemo(() => buildComparisonRows(props.variants), [props.variants]);

  const visibleRows = useMemo(() => {
    const filter = props.pathFilter.trim().toLowerCase();

    return rows.filter(row => {
      if (props.differencesOnly && !row.differs) {
        return false;
      }

      if (filter && !row.path.toLowerCase().includes(filter)) {
        return false;
      }

      return true;
    });
  }, [rows, props.differencesOnly, props.pathFilter]);

  if (props.variants.length === 0) {
    return <p>No variants found.</p>;
  }

  if (props.variants.length === 1) {
    return <p>This product has only one variant — nothing to compare.</p>;
  }

  return (
    <div className="overflow-x-auto border border-solid border-gray-2024 rounded-[4px]">
      <table className="w-full border-collapse text-left text-x2">
        <thead>
          <tr className="bg-gray-light-2024">
            <th className="sticky left-0 z-[1] min-w-[220px] border-b border-solid border-gray-2024 bg-gray-light-2024 px-3 py-2 font-futura-500 text-x2ss">
              Field
            </th>
            {props.variants.map(variant => (
              <th
                key={variant.id}
                className={cn(
                  "min-w-[240px] border-b border-solid border-gray-2024 px-3 py-2 align-top font-futura-500",
                  props.activeVariantSku === variant.sku && "bg-green-50",
                )}
              >
                <div>{variant.label}</div>
                <div className="mt-[4px] font-futura-400 text-x2 text-gray-dark-2024">{variant.sku}</div>
                <div className="font-futura-400 text-x2 text-gray-dark-2024">{variant.key}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(row => {
            const rowBackground = row.differs ? "bg-amber-50" : "bg-gray-light-2024";

            return (
              <tr key={row.path} className={rowBackground}>
                <td
                  className={cn(
                    "sticky left-0 z-[1] border-b border-solid border-gray-2024 px-3 py-2 align-top font-mono text-x2 tracking-[-2px] text-x2s",
                    rowBackground,
                  )}
                >
                  {row.path}
                </td>
                {row.cells.map((cell, index) => (
                  <td
                    key={`${row.path}-${index}`}
                    className={cn(
                      "border-b border-solid border-gray-2024 px-3 py-2 align-top text-x2",
                      rowBackground,
                      row.cellKind === "rich-text" && "font-futura-400 break-words whitespace-pre-wrap",
                      row.cellKind === "text" && "font-mono break-all whitespace-pre-wrap",
                      row.cellKind === "attribute" && "font-futura-400",
                      (row.cellKind === "image" || row.cellKind === "media-gallery" || row.cellKind === "attribute") &&
                        "align-middle",
                    )}
                  >
                    <ComparisonCell cell={cell} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {visibleRows.length === 0 && (
        <p className="px-3 py-[16px] text-gray-dark-2024">No rows match the current filters.</p>
      )}
    </div>
  );
}

const PdpVariantsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pathInput, setPathInput] = useState(searchParams.get("path") ?? "");
  const [submittedPath, setSubmittedPath] = useState(normalizePdpPath(searchParams.get("path") ?? ""));
  const [differencesOnly, setDifferencesOnly] = useState(searchParams.get("differencesOnly") !== "false");
  const [pathFilter, setPathFilter] = useState(searchParams.get("filter") ?? "");

  useEffect(() => {
    const params: Record<string, string> = {};
    if (submittedPath) {
      params.path = submittedPath;
    }
    if (!differencesOnly) {
      params.differencesOnly = "false";
    }
    if (pathFilter) {
      params.filter = pathFilter;
    }
    setSearchParams(params);
  }, [submittedPath, differencesOnly, pathFilter, setSearchParams]);

  const { data, error, isFetching, refetch } = useQuery<PdpPageResponse>({
    queryKey: ["toolbelt", "pdp-variants", submittedPath],
    enabled: submittedPath.length > 0,
    queryFn: async () => {
      const response = await fetch(`${STOREFRONT_PATH_PREFIX}${submittedPath}`);
      if (!response.ok) {
        throw new Error(`Failed to load PDP (${response.status})`);
      }
      return response.json() as Promise<PdpPageResponse>;
    },
  });

  const selectedProduct = useMemo(() => {
    if (!data) {
      return undefined;
    }

    if (data.dataOrError._tag === "Left") {
      return undefined;
    }

    return data.dataOrError.right.selectedProduct;
  }, [data]);

  const pageValidationErrors = useMemo(() => {
    if (!data) {
      return undefined;
    }

    if (data.dataOrError._tag === "Both") {
      return data.dataOrError.left;
    }

    if (data.dataOrError._tag === "Left") {
      return data.dataOrError.left;
    }

    return undefined;
  }, [data]);

  const variantGroups = useMemo(() => extractVariantGroups(selectedProduct), [selectedProduct]);

  const differingFieldCount = useMemo(() => {
    return variantGroups.reduce((total, group) => {
      const rows = buildComparisonRows(group.variants);
      return total + rows.filter(row => row.differs).length;
    }, 0);
  }, [variantGroups]);

  return (
    <ContentContainer>
      <div>
        <h1 className="text-headline-1">PDP variant comparison</h1>
        <p className="mt-2 text-x2 text-gray-dark-2024">
          Load a product detail page and compare all variants side by side. Rows where values differ are highlighted.
        </p>

        <Spacer size="md" />

        <form
          className="grid gap-3"
          onSubmit={event => {
            event.preventDefault();
            setSubmittedPath(normalizePdpPath(pathInput));
          }}
        >
          <label className="grid gap-[6px]">
            <span className="font-futura-500 text-x2">PDP path</span>
            <input
              className="w-full rounded-[4px] border border-solid border-gray-2024 px-3 py-2 font-mono text-x2"
              placeholder="/at/en/hunting/products/.../product-key or .../SKU"
              value={pathInput}
              onChange={event => setPathInput(event.target.value)}
            />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <SwarovskiButton variant="green" as="button" type="submit">
              Load PDP
            </SwarovskiButton>
            {submittedPath && (
              <SwarovskiButton variant="white" as="button" type="button" onClick={() => void refetch()}>
                Refresh
              </SwarovskiButton>
            )}
          </div>
        </form>

        <Spacer size="md" />

        {submittedPath && (
          <div className="rounded-[4px] border border-solid border-gray-2024 bg-gray-light-2024 px-3 py-2 font-mono text-x2 break-all">
            {STOREFRONT_PATH_PREFIX}
            {submittedPath}
          </div>
        )}

        <Spacer size="md" />

        {isFetching && <p>Loading PDP data…</p>}
        {error && <pre className="text-red-600">{error instanceof Error ? error.message : String(error)}</pre>}

        {data && !PDP_PAGE_TYPES.has(data.__typename) && (
          <p>
            This path is a <strong>{data.__typename}</strong> page, not a product detail page. Use a product or variant
            PDP URL.
          </p>
        )}

        {pageValidationErrors !== undefined && (
          <div>
            <p className="text-red-600">Page data has validation errors (partial data may still render below).</p>
            <pre className="mt-2 overflow-x-auto">{JSON.stringify(pageValidationErrors, null, 2)}</pre>
          </div>
        )}

        {selectedProduct && (
          <>
            <div className="grid gap-1">
              <h2 className="text-headline-2">{selectedProduct.name}</h2>
              <div className="font-mono text-x2">
                key: {selectedProduct.key} · path: {selectedProduct.path}
              </div>
              <div className="text-x2 text-gray-dark-2024">
                page type: {data?.__typename} · differing fields: {differingFieldCount}
              </div>
            </div>

            <Spacer size="md" />

            <div className="flex flex-wrap items-center gap-[16px]">
              <label className="inline-flex items-center gap-2 text-x2">
                <input
                  type="checkbox"
                  checked={differencesOnly}
                  onChange={event => setDifferencesOnly(event.target.checked)}
                />
                Show differences only
              </label>

              <label className="inline-flex min-w-[240px] flex-1 items-center gap-2 text-x2">
                <span className="shrink-0">Filter fields</span>
                <input
                  className="w-full rounded-[4px] border border-solid border-gray-2024 px-2 py-[4px] font-mono text-x2"
                  placeholder="e.g. attributes.unique"
                  value={pathFilter}
                  onChange={event => setPathFilter(event.target.value)}
                />
              </label>
            </div>

            <Spacer size="md" />

            {variantGroups.map(group => (
              <section key={group.id} className="mb-[32px]">
                {variantGroups.length > 1 && <h3 className="mb-3 text-headline-3">{group.title}</h3>}
                <VariantComparisonTable
                  variants={group.variants}
                  differencesOnly={differencesOnly}
                  pathFilter={pathFilter}
                  activeVariantSku={data?.page.variantSku}
                />
              </section>
            ))}
          </>
        )}
      </div>
    </ContentContainer>
  );
};

export const ToolbeltPdpVariantsRoute: React.FC = () => {
  const isMounted = useMounted();

  if (!isMounted) {
    return null;
  }

  return <PdpVariantsPage />;
};

export default ToolbeltPdpVariantsRoute;
